if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.zoomStates = new WeakMap();
        this.maxScale = 4;
        this.desktopMediaQuery = window.matchMedia('(min-width: 750px)');
        this.handleThumbnailClick = this.handleThumbnailClick.bind(this);
        this.handleThumbnailPointerUp = this.handleThumbnailPointerUp.bind(this);
        this.handleDesktopProtection = this.handleDesktopProtection.bind(this);
        this.handleMediaTapClose = this.handleMediaTapClose.bind(this);
      }

      connectedCallback() {
        super.connectedCallback();
        if (this.dataset.thumbnailBound === 'true') return;
        this.dataset.thumbnailBound = 'true';
        this.addEventListener('click', this.handleThumbnailClick);
        this.addEventListener('pointerup', this.handleThumbnailPointerUp, true);
        this.addEventListener('contextmenu', this.handleDesktopProtection);
        this.addEventListener('dragstart', this.handleDesktopProtection);
        this.addEventListener('click', this.handleMediaTapClose);
      }

      hide() {
        this.resetZoomables();
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
        this.initZoomables();
      }

      showActiveMedia() {
        const targetId = this.openedBy?.getAttribute('data-media-id');
        if (!targetId) return;
        this.setActiveMedia(targetId);
      }

      handleThumbnailClick(event) {
        const thumbnail = event.target.closest('[data-modal-thumbnail-target]');
        if (!thumbnail || !this.contains(thumbnail)) return;

        this.setActiveMedia(thumbnail.dataset.modalThumbnailTarget);
      }

      handleThumbnailPointerUp(event) {
        if (!event.target.closest('[data-modal-thumbnail-target]')) return;
        event.stopPropagation();
      }

      handleMediaTapClose(event) {
        if (this.desktopMediaQuery.matches) return;
        if (event.target.closest('[data-modal-thumbnail-target], .product-media-modal__toggle')) return;

        const zoomable = event.target.closest('[data-image-zoom]');
        if (zoomable) {
          const state = this.zoomStates.get(zoomable);
          if (state?.scale > 1.01) return;
        }

        if (
          event.target.closest(
            '.product-media-modal__image, .product-media-modal__image-zoom, .product-media-modal__image-element'
          )
        ) {
          this.hide();
        }
      }

      setActiveMedia(targetId) {
        this.querySelectorAll('.active[data-media-id]').forEach((element) => {
          element.classList.remove('active');
        });

        const activeMedia = this.querySelector(`.product-media-modal__content [data-media-id="${targetId}"]`);
        if (!activeMedia) return;

        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        this.hydrateModalImage(activeMedia, targetId);
        activeMedia.classList.add('active');

        const container = this.querySelector('[role="document"]');
        const isMobileDetail = this.dataset.modalMode === 'detail' && window.matchMedia('(max-width: 749px)').matches;

        activeMedia.scrollIntoView({
          behavior: 'auto',
          block: isMobileDetail ? 'start' : 'center',
          inline: 'center',
        });

        if (this.dataset.modalMode !== 'detail' && !isMobileDetail) {
          container.scrollLeft = Math.max((activeMedia.offsetWidth - container.clientWidth) / 2, 0);
        } else {
          container.scrollLeft = 0;
        }

        this.syncActiveThumbnail(targetId);

        if (
          activeMedia.nodeName === 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        ) {
          activeMedia.loadContent();
        }
      }

      syncActiveThumbnail(targetId) {
        const thumbnails = this.querySelectorAll('[data-modal-thumbnail-target]');
        if (!thumbnails.length) return;

        thumbnails.forEach((thumbnail) => {
          const isActive = thumbnail.dataset.modalThumbnailTarget === targetId;
          thumbnail.toggleAttribute('aria-current', isActive);

          if (isActive) {
            thumbnail.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
          }
        });
      }

      initZoomables() {
        this.querySelectorAll('[data-image-zoom]').forEach((zoomable) => {
          if (zoomable.dataset.zoomBound === 'true') return;

          zoomable.dataset.zoomBound = 'true';
          this.zoomStates.set(zoomable, this.defaultZoomState());

          zoomable.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: false });
          zoomable.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false });
          zoomable.addEventListener('touchend', this.handleTouchEnd.bind(this));
          zoomable.addEventListener('touchcancel', this.handleTouchEnd.bind(this));
        });
      }

      handleTouchStart(event) {
        const zoomable = event.currentTarget;
        const state = this.zoomStates.get(zoomable) || this.defaultZoomState();

        if (event.touches.length === 2) {
          state.startDistance = this.getDistance(event.touches[0], event.touches[1]);
          state.startScale = state.scale;
          state.lastPanX = this.getMidpoint(event.touches[0], event.touches[1]).x;
          state.lastPanY = this.getMidpoint(event.touches[0], event.touches[1]).y;
        } else if (event.touches.length === 1 && state.scale > 1) {
          state.lastPanX = event.touches[0].clientX;
          state.lastPanY = event.touches[0].clientY;
        }

        this.zoomStates.set(zoomable, state);
      }

      handleTouchMove(event) {
        const zoomable = event.currentTarget;
        const state = this.zoomStates.get(zoomable) || this.defaultZoomState();

        if (event.touches.length === 2) {
          event.preventDefault();
          const distance = this.getDistance(event.touches[0], event.touches[1]);
          const midpoint = this.getMidpoint(event.touches[0], event.touches[1]);
          state.scale = this.clamp((distance / state.startDistance) * state.startScale, 1, this.maxScale);

          if (state.scale > 1) {
            state.translateX += midpoint.x - state.lastPanX;
            state.translateY += midpoint.y - state.lastPanY;
            state.lastPanX = midpoint.x;
            state.lastPanY = midpoint.y;
          }
        } else if (event.touches.length === 1 && state.scale > 1) {
          event.preventDefault();
          const touch = event.touches[0];
          state.translateX += touch.clientX - state.lastPanX;
          state.translateY += touch.clientY - state.lastPanY;
          state.lastPanX = touch.clientX;
          state.lastPanY = touch.clientY;
        }

        if (state.scale <= 1) {
          state.translateX = 0;
          state.translateY = 0;
        }

        this.zoomStates.set(zoomable, state);
        this.applyTransform(zoomable, state);
      }

      handleTouchEnd(event) {
        const zoomable = event.currentTarget;
        const state = this.zoomStates.get(zoomable) || this.defaultZoomState();

        if (event.touches.length === 0 && state.scale <= 1.01) {
          this.resetZoomable(zoomable);
          return;
        }

        if (event.touches.length === 1 && state.scale > 1) {
          state.lastPanX = event.touches[0].clientX;
          state.lastPanY = event.touches[0].clientY;
        }

        this.zoomStates.set(zoomable, state);
      }

      applyTransform(zoomable, state) {
        const panTarget = zoomable.querySelector('[data-image-pan]');
        if (!panTarget) return;

        zoomable.classList.toggle('is-zoomed', state.scale > 1);
        panTarget.style.transform = `translate(${state.translateX}px, ${state.translateY}px) scale(${state.scale})`;
      }

      resetZoomables() {
        this.querySelectorAll('[data-image-zoom]').forEach((zoomable) => {
          this.resetZoomable(zoomable);
        });
      }

      resetZoomable(zoomable) {
        const state = this.defaultZoomState();
        this.zoomStates.set(zoomable, state);
        this.applyTransform(zoomable, state);
      }

      defaultZoomState() {
        return {
          scale: 1,
          startScale: 1,
          startDistance: 1,
          translateX: 0,
          translateY: 0,
          lastPanX: 0,
          lastPanY: 0,
        };
      }

      getDistance(touchA, touchB) {
        return Math.hypot(touchA.clientX - touchB.clientX, touchA.clientY - touchB.clientY);
      }

      getMidpoint(touchA, touchB) {
        return {
          x: (touchA.clientX + touchB.clientX) / 2,
          y: (touchA.clientY + touchB.clientY) / 2,
        };
      }

      clamp(value, min, max) {
        return Math.min(Math.max(value, min), max);
      }

      handleDesktopProtection(event) {
        if (!this.desktopMediaQuery.matches) return;
        if (
          !event.target.closest(
            '.product-media-modal__image, .product-media-modal__image-zoom, .product-media-modal__image-element, .product-media-modal__thumbnail'
          )
        ) {
          return;
        }
        event.preventDefault();
      }

      hydrateModalImage(activeMedia, targetId) {
        const modalImage = activeMedia.querySelector('[data-modal-image]');
        if (!modalImage || modalImage.dataset.imageReady === 'true') return;

        const galleryImage = this.getGalleryImage(targetId);
        if (galleryImage?.currentSrc) {
          modalImage.src = galleryImage.currentSrc;
        } else if (modalImage.dataset.src) {
          modalImage.src = modalImage.dataset.src;
        }

        if (modalImage.dataset.sizes) {
          modalImage.sizes = modalImage.dataset.sizes;
        }

        if (modalImage.dataset.srcset) {
          modalImage.srcset = modalImage.dataset.srcset;
        }

        modalImage.loading = 'eager';
        modalImage.decoding = 'async';
        modalImage.dataset.imageReady = 'true';
      }

      getGalleryImage(targetId) {
        const openerImage = this.openedBy?.closest('.product__modal-opener')?.querySelector('.product__media img');
        if (openerImage && this.openedBy?.getAttribute('data-media-id') === targetId) {
          return openerImage;
        }

        return document.querySelector(
          `.product__media-item[data-media-id$="-${targetId}"] .product__media img`
        );
      }
    }
  );
}
